(() => {
  if (customElements.get('merchant-withdrawal')) return;
  // German defaults; the snippet passes translated texts as JSON (.wr-i18n).
  const DEFAULT_TEXT = {
    locale: 'de',
    review_name: 'Name', review_email: 'E-Mail', review_contract: 'Vertrag / Bestellung',
    review_details: 'Vertragsteil / ergänzende Angaben', review_whole_contract: 'Gesamter angegebener Vertrag',
    endpoint_invalid: 'Der Endpunkt ist nicht korrekt eingerichtet. Bitte den Händler direkt kontaktieren.',
    prepare_failed: 'Das Formular konnte nicht vorbereitet werden.',
    no_response: 'Keine eindeutige Serverantwort. Bitte mit denselben Angaben erneut versuchen oder den Händler kontaktieren.',
    reopen: 'Bitte das Formular schließen und erneut öffnen oder den Händler direkt kontaktieren.',
    sending: 'Ihr Widerruf wird übermittelt …',
    success: 'Ihr Widerruf ist eingegangen. Eingang: [time]. Referenz: [reference]. Die Eingangsbestätigung wird per E-Mail versendet.',
    error_expired: 'Das Formular ist abgelaufen. Bitte schließen und erneut öffnen.',
    error_origin: 'Widerrufe über diese Shop-Adresse sind nicht freigeschaltet. Bitte den Händler direkt kontaktieren.',
    error_conflict: 'Diese Übermittlung wurde bereits mit anderen Angaben gespeichert.',
    error_invalid: 'Bitte die Formularangaben prüfen: Name, gültige E-Mail-Adresse und Vertragsangabe sind erforderlich.',
    error_rate_limit: 'Zu viele Anfragen. Bitte später erneut versuchen oder den Händler direkt kontaktieren.',
    error_unavailable: 'Übermittlung derzeit nicht möglich. Bitte erneut versuchen oder den Händler direkt kontaktieren.'
  };
  const ERROR_BY_STATUS = {400: 'error_expired', 403: 'error_origin', 409: 'error_conflict', 422: 'error_invalid', 429: 'error_rate_limit'};
  class Withdrawal extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      let text = {};
      try { text = JSON.parse(this.querySelector('.wr-i18n')?.textContent || '{}'); } catch (error) { text = {}; }
      this.text = {...DEFAULT_TEXT, ...text};
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.status = this.querySelector('.wr-status');
      this.fields = this.querySelector('.wr-fields');
      this.review = this.querySelector('.wr-review');
      this.confirm = this.querySelector('.wr-confirm');
      this.launch = this.querySelector('.wr-launch');
      this.launch.addEventListener('click', async (event) => {
        if (!this.dialog.showModal) return;
        event.preventDefault();
        this.dialog.showModal();
        if (this.done) return;
        if (!this.token) {
          try {
            const url = new URL(this.dataset.endpoint);
            if (url.protocol !== 'https:' && !(url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname))) throw new Error(this.text.endpoint_invalid);
            this.endpoint = url.href.replace(/\/$/, '');
            this.token = (await this.request('/api/token')).token;
            if (!this.token) throw new Error(this.text.prepare_failed);
            this.status.textContent = '';
          } catch (error) { this.status.textContent = error.message; }
        }
      });
      this.querySelector('.wr-close').addEventListener('click', () => this.dialog.close());
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!this.form.reportValidity()) return;
        this.payload = Object.fromEntries(new FormData(this.form));
        const dl = this.querySelector('dl');
        dl.replaceChildren();
        [[this.text.review_name, 'name'], [this.text.review_email, 'email'], [this.text.review_contract, 'contract'], [this.text.review_details, 'details']].forEach(([label, key]) => {
          const dt = document.createElement('dt'); const dd = document.createElement('dd');
          dt.textContent = label; dd.textContent = this.payload[key] || this.text.review_whole_contract;
          dl.append(dt, dd);
        });
        this.fields.hidden = true; this.review.hidden = false;
        this.confirm.focus();
      });
      this.querySelector('.wr-back').addEventListener('click', () => {
        if (this.pending) return;
        this.review.hidden = true; this.fields.hidden = false;
        this.form.elements.name.focus();
      });
      this.confirm.addEventListener('click', () => this.submitWithdrawal());
    }
    async request(path, payload) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(this.endpoint + path, {
          method: payload ? 'POST' : 'GET', mode: 'cors', credentials: 'omit',
          headers: payload ? {'Content-Type': 'application/json'} : {},
          body: payload ? JSON.stringify(payload) : undefined, signal: controller.signal
        });
        const result = await response.json();
        if (!response.ok) {
          // Server messages are German only; show the translated message for the status.
          const error = new Error(this.text[ERROR_BY_STATUS[response.status] || 'error_unavailable']);
          error.status = response.status;
          throw error;
        }
        return result;
      } catch (error) {
        if (error.name === 'AbortError' || error instanceof TypeError || error instanceof SyntaxError) throw new Error(this.text.no_response);
        throw error;
      } finally { clearTimeout(timer); }
    }
    async submitWithdrawal() {
      if (this.pending || this.done) return;
      if (!this.token) { this.status.textContent = this.text.reopen; return; }
      this.pending = true; this.confirm.disabled = true;
      this.querySelector('.wr-back').disabled = true;
      this.status.textContent = this.text.sending;
      try {
        const result = await this.request('/api/withdrawals', {...this.payload, token: this.token, confirmed: true, locale: this.text.locale});
        this.done = true; this.form.hidden = true;
        this.status.textContent = this.text.success
          .replace('[time]', new Date(result.received_at).toLocaleString(this.text.locale || undefined))
          .replace('[reference]', result.reference);
        this.status.focus();
      } catch (error) {
        // An expired or invalid token needs a new one: reopening the dialog fetches it.
        if (error.status === 400) this.token = null;
        this.status.textContent = error.message;
      }
      finally { this.pending = false; this.confirm.disabled = false; this.querySelector('.wr-back').disabled = false; }
    }
  }
  const register = () => {
    if (!customElements.get('merchant-withdrawal')) customElements.define('merchant-withdrawal', Withdrawal);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', register, {once: true});
  else register();
})();
